/**
 * Coquille de mise en page (D§3, D§7).
 *
 * **Deux vraies mises en page, pas une étirée.** D§7 interdit explicitement de servir
 * la disposition mobile agrandie sur desktop. Ici :
 *
 * - < 1024 px : dock de 4 entrées en bas, contenu dans un tiroir escamotable ;
 * - ≥ 1024 px : rail de navigation vertical, panneau persistant à droite, ville visible
 *   à côté en permanence — aucun tiroir, rien qui recouvre la ville.
 *
 * Le contenu est **le même nœud DOM** dans les deux cas : au franchissement du seuil,
 * la coquille le déplace d'un hôte à l'autre. Deux copies divergeraient au premier bug,
 * et doubleraient les écouteurs.
 *
 * La coquille possède la navigation ; les écrans ne savent pas où ils sont montés.
 * C'est ce qui permet à un écran écrit pour le tiroir d'apparaître dans le panneau
 * latéral sans une ligne de changement.
 */

import { t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import { createEmptyState } from './components';
import { createSheet } from './components/overlays';
import type { SheetHandle, SheetPosition } from './components/overlays';
import { icon } from './icons';
import type { IconName } from './icons';

/** Les quatre sections du D§3. L'ordre est celui du dock, de gauche à droite. */
export const SHELL_SECTIONS = ['build', 'city', 'social', 'progress', 'games'] as const;

export type ShellSection = (typeof SHELL_SECTIONS)[number];

interface SectionSpec {
  readonly id: ShellSection;
  readonly icon: IconName;
  readonly labelKey: TranslationKey;
}

const SECTIONS: readonly SectionSpec[] = [
  { id: 'build', icon: 'build', labelKey: 'nav.build' },
  { id: 'city', icon: 'city', labelKey: 'nav.city' },
  { id: 'social', icon: 'social', labelKey: 'nav.social' },
  { id: 'progress', icon: 'trophy', labelKey: 'nav.progress' },
  // « Mes jeux » — cinquième entrée, demandée par Benoit (18 août 2026) : la vitrine
  // croisée mérite son onglet, pas un strapontin sous la Progression.
  { id: 'games', icon: 'gamepad', labelKey: 'nav.games' },
];

/**
 * Seuil des deux mises en page (D§7). En pixels CSS, pas en pixels physiques : un
 * téléphone à DPR 3 fait 1170 px physiques et doit rester en mise en page mobile.
 */
export const DESKTOP_BREAKPOINT = 1024;

/**
 * Quand poser le panneau À CÔTÉ de la ville plutôt qu'un tiroir par-dessus.
 *
 * ── Ce que la largeur seule ne voyait pas ────────────────────────────────────
 *
 * La condition était `largeur ≥ 1024`. Un téléphone en PAYSAGE mesure donc 780 × 390 et
 * recevait la mise en page portrait telle quelle. Mesuré à l'écran : bandeau 160 px +
 * tiroir 304 px = **464 px sur 390 disponibles**. La ville était intégralement
 * recouverte — exactement ce que D§7 interdit (« pas un HUD mobile étiré », « orientation
 * paysage gérée sur mobile »).
 *
 * Ce qui décide n'est pas la largeur mais la **hauteur disponible** : un tiroir venant du
 * bas ne peut pas cohabiter avec un bandeau sur 390 px. Un écran court et large est plus
 * proche d'un bureau étroit que d'un téléphone debout, et c'est le panneau latéral qu'il
 * lui faut — la coquille sait déjà le faire, elle déplace le même nœud DOM.
 *
 * 560 px de large : un iPhone SE en paysage fait 568 px, et il doit en bénéficier — c'est
 * le cas le plus étouffé de tous. En dessous, aucune des deux dispositions ne tient, et
 * on garde le tiroir plutôt que d'écraser la ville par la droite.
 */
const SIDE_LAYOUT_QUERY = `(min-width: ${DESKTOP_BREAKPOINT}px), (min-width: 560px) and (max-height: 480px)`;

export interface ShellHandle {
  readonly el: HTMLElement;
  /** Installe le contenu d'une section. À appeler une fois par section. */
  mount(section: ShellSection, content: HTMLElement): void;
  /** Bascule sur une section. */
  select(section: ShellSection): void;
  getSection(): ShellSection;
  /** Vrai quand la mise en page desktop est active. */
  isDesktop(): boolean;
  /**
   * Pastille de notification sur une entrée du dock.
   *
   * Une récompense qui attend dans un panneau fermé n'existe pas : le joueur ne va pas
   * inspecter les quatre sections pour vérifier. La pastille est ce qui transforme une
   * quête terminée en raison de rouvrir le jeu.
   */
  setBadge(section: ShellSection, count: number): void;
  /**
   * Lueur persistante sur une entrée du dock — l'ère disponible (E§0.1 : le moment
   * le plus rare du jeu mérite plus qu'un chiffre dans une pastille).
   */
  setGlow(section: ShellSection, on: boolean): void;
  /** Fait pulser une entrée du dock — le doigt de la mascotte du tutoriel. */
  pulse(section: ShellSection | null): void;
  /** Replie ou déplie le panneau latéral (desktop uniquement). */
  setCollapsed(collapsed: boolean): void;
  isCollapsed(): boolean;
  /**
   * Efface le contenu pour laisser voir la ville. Sans effet en desktop : le panneau
   * latéral ne recouvre jamais la ville, il n'y a donc rien à escamoter.
   */
  collapse(): void;
  /** Réaffiche le contenu après un `collapse()`. */
  reveal(): void;
  /** Met à jour le sous-titre du panneau (le solde en desktop, l'astuce en mobile). */
  setHint(text: string): void;
  destroy(): void;
}

export interface ShellOptions {
  /** Section affichée au démarrage. */
  readonly initial?: ShellSection;
  readonly onSectionChange?: (section: ShellSection) => void;
}

export function createShell(options: ShellOptions = {}): ShellHandle {
  const el = document.createElement('div');
  el.className = 'v-shell';

  // ── Navigation ────────────────────────────────────────────────────────────
  // Un seul jeu de boutons, déplacé entre le dock et le rail. Deux jeux voudraient
  // dire deux états `aria-selected` à tenir synchronisés.
  const nav = document.createElement('nav');
  nav.className = 'v-nav';
  nav.setAttribute('aria-label', t('nav.label'));

  const buttons = new Map<ShellSection, HTMLButtonElement>();
  const badges = new Map<ShellSection, HTMLElement>();

  for (const spec of SECTIONS) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'v-nav__item';
    button.dataset.section = spec.id;
    button.setAttribute('aria-selected', 'false');
    button.append(icon(spec.icon, { size: 'large' }));

    const label = document.createElement('span');
    label.className = 'v-nav__label';
    label.textContent = t(spec.labelKey);
    button.append(label);

    button.addEventListener('click', () => {
      // Ouvrir le tiroir est une réponse au GESTE, pas une conséquence de la sélection.
      // Confondre les deux faisait s'ouvrir le tiroir à mi-hauteur au démarrage, avant
      // le moindre geste — la ville était masquée à l'arrivée, ce qu'E§4 interdit.
      // Toucher l'entrée DÉJÀ active referme le panneau : c'est le geste attendu
      // dans un jeu, et il évite d'avoir à viser un petit chevron.
      if (desktop && spec.id === current) {
        setCollapsed(!collapsed);
        return;
      }
      setCollapsed(false);
      select(spec.id);
      /*
        Le dock ouvre la bande ENTIÈREMENT (cran 2), pas à mi-course.

        La bande fait 27 % de l'écran et son bord bas est calé au haut du dock : ouverte
        en plein, elle affiche exactement son contenu et rien n'est coupé. À un cran
        intermédiaire, sa dernière carte se retrouvait tranchée par le dock lui-même. Les
        crans partiels restent atteignables au glissement, où c'est le joueur qui décide.
      */
      if (!desktop) sheet.setPosition(2);
    });

    const badge = document.createElement('i');
    badge.className = 'v-nav__badge';
    badge.hidden = true;
    button.append(badge);
    badges.set(spec.id, badge);

    buttons.set(spec.id, button);
    nav.append(button);
  }

  // ── Hôtes de contenu ──────────────────────────────────────────────────────
  const sheet: SheetHandle = createSheet({
    title: t('nav.build'),
    hint: t('shop.hint'),
    // Position basse au démarrage : E§4 veut que la ville soit visible en arrivant.
    // À mi-hauteur, le tiroir mangeait la moitié de l'écran avant le premier geste.
    position: 0,
  });
  sheet.el.classList.add('v-shell__sheet');

  const side = document.createElement('aside');
  side.className = 'v-shell__side v-panel v-panel--raised';

  const sideHead = document.createElement('div');
  sideHead.className = 'v-shell__side-head';

  const sideTitle = document.createElement('h2');
  sideTitle.className = 'v-shell__side-title';
  sideTitle.textContent = t('nav.build');

  const sideHint = document.createElement('span');
  sideHint.className = 'v-shell__side-hint';

  /*
    Le panneau latéral devait pouvoir se REFERMER. Permanent et sans échappatoire, il
    confisque un tiers de l'écran d'un jeu dont tout l'intérêt est de regarder sa ville.
    Le chevron le replie sur son rail ; toucher l'entrée active du rail fait de même.
  */
  const collapse = document.createElement('button');
  collapse.type = 'button';
  collapse.className = 'v-shell__collapse';
  collapse.setAttribute('aria-label', t('city.collapse'));
  collapse.append(icon('chevron'));
  collapse.addEventListener('click', () => {
    setCollapsed(!collapsed);
  });

  sideHead.append(sideTitle, sideHint, collapse);

  const sideBody = document.createElement('div');
  sideBody.className = 'v-shell__side-body';
  side.append(sideHead, sideBody);

  el.append(nav, sheet.el, side);

  // ── Sections ──────────────────────────────────────────────────────────────
  const panes = new Map<ShellSection, HTMLElement>();

  for (const spec of SECTIONS) {
    const pane = document.createElement('div');
    pane.className = 'v-shell__pane';
    pane.dataset.section = spec.id;
    pane.hidden = true;
    panes.set(spec.id, pane);
  }

  /**
   * Les trois sections encore à construire ne disent pas « bientôt disponible » — c'est
   * du texte de développeur, qui informe sur l'avancement du chantier au lieu de parler
   * au joueur. D§4 demande qu'un panneau vide raconte quelque chose de chaleureux :
   * chacun annonce donc ce qu'on y trouvera, et donne envie d'y revenir.
   *
   * Le squelette de navigation est celui du D§3 : c'est lui qui absorbera les écrans des
   * phases suivantes, sans redessiner la mise en page.
   */
  const placeholders = new Map<ShellSection, HTMLElement>();

  for (const spec of SECTIONS) {
    const pane = panes.get(spec.id);
    if (pane === undefined) continue;
    const placeholder = createEmptyState({
      icon: spec.icon,
      title: t(`nav.empty.${spec.id}.title` as TranslationKey),
      text: t(`nav.empty.${spec.id}.text` as TranslationKey),
    }).el;
    placeholders.set(spec.id, placeholder);
    pane.append(placeholder);
  }

  let current: ShellSection = options.initial ?? 'build';
  /*
    La valeur INITIALE vient de la même requête que les changements suivants.
    Deux conditions différentes — `innerWidth >= 1024` au départ, `matchMedia` ensuite —
    laissaient une fenêtre morte où l'état de départ contredisait l'état surveillé.
  */
  const query = window.matchMedia(SIDE_LAYOUT_QUERY);
  let desktop = query.matches;
  let collapsed = false;

  function setCollapsed(next: boolean): void {
    collapsed = next;
    el.classList.toggle('v-shell--collapsed', collapsed);
    collapse.setAttribute('aria-label', collapsed ? t('city.expand') : t('city.collapse'));
  }

  function activeHost(): HTMLElement {
    return desktop ? sideBody : sheet.body;
  }

  /** Déplace le volet actif dans l'hôte de la mise en page courante. */
  function place(): void {
    el.classList.toggle('v-shell--desktop', desktop);
    el.classList.toggle('v-shell--compact', !desktop);

    const host = activeHost();
    for (const [id, pane] of panes) {
      const active = id === current;
      pane.hidden = !active;
      if (active && pane.parentElement !== host) {
        host.append(pane);
      } else if (!active && pane.parentElement !== null) {
        pane.remove();
      }
    }

    const label = SECTIONS.find((spec) => spec.id === current)?.labelKey;
    if (label !== undefined) {
      sideTitle.textContent = t(label);
      sheet.el.setAttribute('aria-label', t(label));
    }
  }

  function select(section: ShellSection): void {
    current = section;
    for (const [id, button] of buttons) {
      button.setAttribute('aria-selected', String(id === section));
    }

    /*
      Le TITRE suit la section — dans les deux mises en page.

      Le tiroir et le panneau latéral sont uniques et servent les quatre entrées du dock,
      mais leur titre était posé une fois pour toutes à la création : ouvrir « Social »
      affichait le partage sous l'en-tête « Construire ». Vu à l'écran, et faux pour les
      trois sections sur quatre.
    */
    const spec = SECTIONS.find((candidate) => candidate.id === section);
    if (spec !== undefined) {
      const label = t(spec.labelKey);
      // Seule la boutique a une astuce de glissement à donner ; les autres n'en ont pas,
      // et répéter celle de la boutique serait un contresens.
      sheet.setTitle(label, section === 'build' ? t('shop.hint') : undefined);
      sideTitle.textContent = label;
    }

    /*
      Une section choisie AU DOIGT doit être visible.

      Le tiroir s'ouvre en position basse (E§4 : la ville d'abord). Mais taper une entrée
      du dock est une demande explicite : laisser le tiroir en bas montrait le titre du
      panneau et coupait son contenu sous le dock — mesuré sur « Social », dont le bouton
      de partage tombait hors de l'écran.
    */
    if (!desktop && section !== 'build' && sheet.getPosition() === 0) {
      sheet.setPosition(2);
    }

    place();
    options.onSectionChange?.(section);
  }

  // `matchMedia` surveille la condition sans coût par frame — y compris la rotation de
  // l'appareil, qui change la hauteur sans passer par un événement de redimensionnement
  // exploitable sur tous les navigateurs.
  function onBreakpoint(): void {
    const next = query.matches;
    if (next === desktop) return;
    desktop = next;
    place();
  }

  query.addEventListener('change', onBreakpoint);

  select(current);

  return {
    el,
    /*
      Monter du vrai contenu ÉVINCE le placeholder de la section.

      La version précédente tenait une liste des sections « déjà faites » à sauter. Elle
      s'est désynchronisée deux fois : « Ville » a gardé son état vide au-dessus de ses
      commandes de terrain pendant des versions, et « Social » a fait cohabiter le
      panneau de partage avec une invitation qui promettait exactement la même chose.

      Une liste à maintenir se trompe ; une règle ne se trompe pas. Le placeholder est
      un REPLI : dès qu'il y a mieux à montrer, il disparaît de lui-même.
    */
    mount(section, content) {
      const pane = panes.get(section);
      if (pane === undefined) return;
      placeholders.get(section)?.remove();
      placeholders.delete(section);
      pane.append(content);
    },
    select,
    getSection: () => current,
    isDesktop: () => desktop,
    pulse(section) {
      for (const [id, button] of buttons) {
        button.classList.toggle('v-nav__item--pulse', id === section);
      }
    },

    setGlow(section, on) {
      buttons.get(section)?.classList.toggle('v-nav__item--glow', on);
    },

    setBadge(section, count) {
      const badge = badges.get(section);
      if (badge === undefined) return;

      // Le nombre plutôt qu'un simple point : « 3 » dit qu'il y a de quoi faire, un
      // point dit seulement qu'il se passe quelque chose.
      badge.textContent = count > 9 ? '9+' : String(count);
      badge.hidden = count <= 0;
    },
    setCollapsed,
    isCollapsed: () => collapsed,
    collapse() {
      if (!desktop) sheet.setPosition(0);
    },
    reveal() {
      if (!desktop) sheet.setPosition(2 satisfies SheetPosition);
    },
    setHint(text) {
      sideHint.textContent = text;
      const sheetHint = sheet.el.querySelector('.v-sheet__hint');
      if (sheetHint !== null) sheetHint.textContent = text;
    },
    destroy() {
      query.removeEventListener('change', onBreakpoint);
      sheet.destroy();
      el.remove();
    },
  };
}
